import CloseIcon from '@mui/icons-material/Close'
import { Box, ButtonBase, IconButton, Portal, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useEffect, useMemo, useState } from 'react'

const DEFAULT_HEIGHT = 360

const MediaThumbnailButton = ({ item, isActive, onClick, showLabel, thumbnailAspectRatio }) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      width: '100%',
      minWidth: 0,
      alignSelf: 'start',
      textAlign: 'left',
      borderRadius: 1.25,
      overflow: 'hidden',
      border: '1px solid',
      borderColor: (theme) =>
        isActive
          ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.55 : 0.4)
          : alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.55 : 0.42),
      backgroundColor: (theme) =>
        isActive
          ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.08)
          : alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.42 : 0.72),
      opacity: isActive ? 1 : 0.78,
      transition: 'border-color 140ms ease, background-color 140ms ease, box-shadow 140ms ease, opacity 140ms ease',
      alignItems: 'stretch',
      justifyContent: 'stretch',
      display: 'block',
      ...(showLabel ? {} : { aspectRatio: thumbnailAspectRatio }),
      '&:hover': {
        borderColor: (theme) =>
          isActive
            ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.7 : 0.5)
            : alpha(theme.palette.text.secondary, theme.palette.mode === 'dark' ? 0.55 : 0.36),
        boxShadow: (theme) =>
          theme.palette.mode === 'dark' ? `0 6px 16px ${alpha('#000', 0.28)}` : `0 4px 12px ${alpha('#0f172a', 0.08)}`,
        opacity: 1,
      },
    }}
  >
    <Box sx={{ width: '100%', height: '100%' }}>
      <Box
        sx={{
          aspectRatio: showLabel ? thumbnailAspectRatio : 'auto',
          height: showLabel ? 'auto' : '100%',
          overflow: 'hidden',
          bgcolor: (theme) =>
            isActive
              ? alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.92 : 0.96)
              : alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.62 : 0.74),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {item.renderThumbnail()}
      </Box>
    </Box>
    {showLabel && item.label ? (
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          px: 1,
          py: 0.8,
          color: isActive ? 'primary.main' : 'text.secondary',
          fontWeight: isActive ? 700 : 600,
          lineHeight: 1.25,
        }}
      >
        {item.label}
      </Typography>
    ) : null}
  </ButtonBase>
)

const MediaStackViewer = ({
  items = [],
  empty = null,
  desktopHeight = DEFAULT_HEIGHT,
  showLabels = false,
  aspectRatio = '4 / 3',
}) => {
  const availableItems = useMemo(() => items.filter(Boolean), [items])
  const [activeId, setActiveId] = useState(availableItems[0]?.id ?? null)
  const [previewedItem, setPreviewedItem] = useState(null)

  useEffect(() => {
    if (!availableItems.length) {
      setActiveId(null)
      return
    }
    if (!availableItems.some((item) => item.id === activeId)) {
      setActiveId(availableItems[0].id)
    }
  }, [activeId, availableItems])

  useEffect(() => {
    if (!previewedItem) {
      return undefined
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setPreviewedItem(null)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [previewedItem])

  if (!availableItems.length) return empty

  const activeItem = availableItems.find((item) => item.id === activeId) || availableItems[0]
  const previewContent = previewedItem?.renderPreview ? previewedItem.renderPreview() : previewedItem?.renderMain()

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 92px' },
        gap: 1,
        alignItems: 'stretch',
      }}
    >
      <Box
        onClick={() => setPreviewedItem(activeItem)}
        sx={{
          minWidth: 0,
          width: { xs: '100%', md: 'auto' },
          height: { xs: 'auto', md: desktopHeight },
          minHeight: { xs: 260, md: desktopHeight },
          maxHeight: { xs: 360, md: desktopHeight },
          maxWidth: '100%',
          aspectRatio,
          borderRadius: 2,
          overflow: 'hidden',
          boxSizing: 'border-box',
          border: '1px solid',
          borderColor: (theme) => alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.55 : 0.42),
          bgcolor: (theme) =>
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.default, 0.72)
              : alpha(theme.palette.background.default, 0.92),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'zoom-in',
        }}
      >
        {activeItem.renderMain()}
      </Box>
      <Box
        sx={{
          minWidth: 0,
          display: 'grid',
          gridTemplateColumns: { xs: `repeat(${availableItems.length}, minmax(0, 1fr))`, md: '1fr' },
          gridAutoRows: { md: 'max-content' },
          gap: 0.75,
          maxHeight: { md: desktopHeight },
          overflow: 'auto',
          pr: { md: 0.25 },
          alignContent: { md: 'start' },
        }}
      >
        {availableItems.map((item) => (
          <MediaThumbnailButton
            key={item.id}
            item={item}
            isActive={item.id === activeItem.id}
            onClick={() => setActiveId(item.id)}
            showLabel={showLabels}
            thumbnailAspectRatio={aspectRatio}
          />
        ))}
      </Box>
      {previewedItem ? (
        <Portal>
          <Box
            onClick={() => setPreviewedItem(null)}
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: (theme) => theme.zIndex.modal + 10,
              backgroundColor: 'rgba(0, 0, 0, 0.28)',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              pt: { xs: 6, md: 8 },
              px: 2,
              pb: 2,
            }}
          >
            <Box
              onClick={(event) => event.stopPropagation()}
              sx={{
                position: 'relative',
                width: 'min(92vw, 1200px)',
                height: 'min(90vh, 1000px)',
                overflow: 'hidden',
                borderRadius: 1,
                boxShadow: 8,
                backgroundColor: 'rgba(0, 0, 0, 0.72)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
              }}
            >
              <IconButton
                onClick={() => setPreviewedItem(null)}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  zIndex: 1,
                  color: 'common.white',
                  bgcolor: 'rgba(15, 23, 42, 0.4)',
                  border: '1px solid',
                  borderColor: 'rgba(255, 255, 255, 0.14)',
                  '&:hover': {
                    bgcolor: 'rgba(15, 23, 42, 0.6)',
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  minHeight: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  ...(previewedItem?.previewSurfaceSx || {}),
                }}
              >
                {previewContent}
              </Box>
            </Box>
          </Box>
        </Portal>
      ) : null}
    </Box>
  )
}

export default MediaStackViewer
